import { useState, useEffect, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Swal from "sweetalert2";
import { useForm, Controller } from 'react-hook-form';
import { RxCross2 } from "react-icons/rx";
import {
    FaCalculator, FaUser, FaShoppingCart, FaBox, FaWarehouse, FaEnvelope,
    FaPhoneAlt, FaMapMarkerAlt, FaHashtag, FaCity, FaFlag, FaRupeeSign,
    FaWallet, FaClipboardList, FaWeightHanging, FaBoxOpen, FaRulerCombined
} from 'react-icons/fa';
import { useMyContext } from '../../../../context/MyContext';
import '../../../../css/custom-scrollbar.css';
import ViewRateForword from '../../../../component/b2c/models/ViewRateForwordShipmet';
import { Base_Url, getpincode, addShipment, allwarehouseslist } from '../../../../config/config';
import { toast } from 'react-toastify';
import Spinner from '../../../../component/spinner/spinner';
import axios from 'axios';

const schema = z.object({
    warehouse: z.string().nonempty("Warehouse is required"),
    firstName: z
        .string().trim().min(1, "First Name is required")
        .regex(/^[a-zA-Z\s\-'\.]+$/, "First Name must only contain alphabetic characters")
        .refine((val) => val.trim().length > 0, { message: "First Name cannot be only spaces" }),
    lastName: z
        .string().trim().min(1, "Last Name is required")
        .regex(/^[a-zA-Z\s\-'\.]+$/, "Last Name must only contain alphabetic characters")
        .refine((val) => val.trim().length > 0, { message: "Last Name cannot be only spaces" }),
    email: z
        .string().optional()
        .refine((val) => !val || z.string().email().safeParse(val).success, { message: "Please enter a valid email address" }),
    mobile: z
        .string().trim()
        .refine((val) => /^\d{10}$/.test(val), { message: "Mobile number must be exactly 10 digits" }),
    address: z
        .string().min(1, "Address is required")
        .regex(/^[^#\\%&;@]*$/, "Address contains invalid characters (# \\ % & @ ; not allowed)"),
    pincode: z
        .string().trim()
        .refine((val) => /^\d{6}$/.test(val), { message: "Pincode must be exactly 6 digits" }),
    city: z
        .string().trim().min(1, "City is required")
        .regex(/^[a-zA-Z][a-zA-Z\s().,'-]*$/, "City contains invalid characters"),
    state: z
        .string().trim().min(1, "State is required")
        .regex(/^[a-zA-Z\s\-]+$/, "State must only contain alphabetic characters"),
    orderId: z
        .string().trim().min(1, "Order ID is required")
        .refine((val) => val.trim().length > 0, { message: "Order ID cannot be only spaces" }),
    invoiceAmount: z
        .string().trim().min(1, "Invoice Amount is required")
        .refine((val) => val.trim().length > 0 && /^\d*\.?\d+$/.test(val), { message: "Invoice Amount must be a valid number" }),
    paymentMode: z
        .string().trim().min(1, "Payment Mode is required")
        .refine((val) => val.trim().length > 0, { message: "Payment Mode cannot be only spaces" }),
    productDes: z
        .string().trim().min(1, "Product Description is required")
        .refine((val) => val.trim().length > 0, { message: "Product Description cannot be only spaces" }),
    codAmount: z
        .string().optional()
        .refine((val) => !val || (val.trim().length > 0 && !/^\s+$/.test(val)), { message: "COD Amount cannot be only spaces" }),
    weight: z.preprocess(
        (val) => (val === '' || val === undefined || val === null) ? undefined : Number(val),
        z.number()
            .int("Weight must be a whole number")
            .min(50, "Weight must be at least 50 gm")
            .max(20000, "Weight must be less than or equal to 20 kg")
    ),
    length: z
        .string().min(1, "Length is required")
        .refine((val) => /^\d*\.?\d+$/.test(val) && parseFloat(val) > 0, { message: "Length must be a positive number" }),
    width: z
        .string().min(1, "Width is required")
        .refine((val) => /^\d*\.?\d+$/.test(val) && parseFloat(val) > 0, { message: "Width must be a positive number" }),
    height: z
        .string().min(1, "Height is required")
        .refine((val) => /^\d*\.?\d+$/.test(val) && parseFloat(val) > 0, { message: "Height must be a positive number" }),
});

const errorText = (msg) => (
    <p className="text-red-500 text-[8px] mt-0.5 font-medium flex items-center gap-1">
        <span className="w-0.5 h-0.5 rounded-full bg-red-500 inline-block" />
        {msg}
    </p>
);

const Field = ({ icon: Icon, label, error, children, isDarkMode }) => (
    <div>
        <label className={`block text-[10px] font-medium tracking-wider mb-0.5 ${isDarkMode ? "text-indigo-200/80" : "text-gray-700"}`}>
            {label}
        </label>
        <div className="relative group">
            <Icon
                size={10}
                className={`absolute left-2.5 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                    ${isDarkMode ? "text-indigo-300/70 group-focus-within:text-indigo-300" : "text-gray-400 group-focus-within:text-black"}`}
            />
            {children}
        </div>
        {error && errorText(error)}
    </div>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, className = "", isDarkMode }) => (
    <div
        className={`relative overflow-hidden rounded-lg border p-2.5 transition-colors
            ${isDarkMode
                ? "bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.12)]"
                : "bg-gray-300/10 border-gray-300"
            } ${className}`}
    >
        <div className="relative flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
                <span className={`flex items-center justify-center w-6 h-6 rounded-lg shrink-0 border
                    ${isDarkMode
                        ? "bg-gradient-to-br from-indigo-600 to-purple-600 text-white border-indigo-400/40 shadow-lg shadow-indigo-900/40"
                        : "bg-blue-600 text-white border-gray-300"
                    }`}>
                    <Icon size={11} />
                </span>
                <div>
                    <h2 className={`text-[12px] font-bold tracking-wide ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                        {title}
                    </h2>
                    {subtitle && (
                        <p className={`text-[10px] mt-0.5 ${isDarkMode ? "text-indigo-200/60" : "text-gray-400"}`}>
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
        </div>
        <div className="relative">{children}</div>
    </div>
);

/* NEW: Searchable dropdown, hoisted to module scope like Field/SectionCard
   so it doesn't get re-created on every render (avoids input focus loss). */
const SearchableSelect = ({ options, value, onChange, placeholder, isDarkMode, inputClassName, id }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find((opt) => opt.value === value);

    const filteredOptions = options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (opt) => {
        onChange(opt);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <input
                id={id}
                type="text"
                readOnly={!isOpen}
                value={isOpen ? search : (selectedOption ? selectedOption.label : '')}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => { setIsOpen(true); setSearch(''); }}
                onClick={() => { setIsOpen(true); setSearch(''); }}
                placeholder={placeholder}
                autoComplete="off"
                className={`${inputClassName} cursor-pointer`}
            />
            {isOpen && (
                <div
                    className={`absolute z-[10000] mt-1 w-full max-h-40 overflow-y-auto rounded-md border shadow-lg custom-scrollbar
                        ${isDarkMode
                            ? "bg-[#0b0b14] border-indigo-400/40"
                            : "bg-white border-gray-300"
                        }`}
                >
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt) => (
                            <div
                                key={opt.value}
                                onMouseDown={() => handleSelect(opt)}
                                className={`px-2.5 py-1.5 text-[10px] cursor-pointer transition-colors
                                    ${isDarkMode
                                        ? "text-indigo-100 hover:bg-indigo-600/30"
                                        : "text-gray-800 hover:bg-gray-100"
                                    } ${opt.value === value ? (isDarkMode ? "bg-indigo-600/20" : "bg-gray-100") : ""}`}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className={`px-2.5 py-1.5 text-[10px] ${isDarkMode ? "text-indigo-300/60" : "text-gray-400"}`}>
                            No warehouse found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default function AddShipmentPopup({ isOpen, onClose, onShipmentAdded }) {
    const { isDarkMode } = useMyContext();
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tableData, setTableData] = useState(null);
    const [wareHouseList, setwareHouseList] = useState([]);
    const [wareHouseId, setwareHouseId] = useState('');
    const [reqBody, setReqBody] = useState([]);
    const [apiError, setApiError] = useState('');

    // Pulls the human-readable reason out of any API error/response shape
    const extractApiMessage = (payload, fallback) => {
        if (!payload) return fallback;
        if (typeof payload === 'string') return payload;
        const pick = (v) => {
            if (!v) return '';
            if (typeof v === 'string') return v;
            if (Array.isArray(v)) return v.map(pick).filter(Boolean).join(', ');
            if (typeof v === 'object') return Object.values(v).map(pick).filter(Boolean).join(', ');
            return String(v);
        };
        return pick(payload.message) || pick(payload.messages) || pick(payload.error) || pick(payload.errors) || fallback;
    };

    const { register, handleSubmit, formState: { errors }, reset, watch, trigger, setValue, control } = useForm({
        resolver: zodResolver(schema),
    });

    const handleViewClick = () => setIsPopupOpen(true);
    const handleClose = () => setIsPopupOpen(false);

    const pincode = watch("pincode");
    const paymentMode = watch("paymentMode");

    useEffect(() => {
        if (pincode && pincode.length === 6) fetchPincodeData(pincode);
    }, [pincode]);

    useEffect(() => {
        if (paymentMode === "prepaid") setValue("codAmount", "0");
        else if (paymentMode === "cod") setValue("codAmount", "");
    }, [paymentMode, setValue]);

    useEffect(() => {
        if (isOpen) {
            fetchWareHouseList();
            reset();
            setApiError('');
            setTableData(null);
        }
    }, [isOpen]);

    const fetchPincodeData = async (pincode) => {
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const urlgetPincode = `${Base_Url}${getpincode}?pincode=${pincode}`;
        try {
            const response = await fetch(urlgetPincode, {
                method: 'GET',
                headers: { 'token': token, 'cus_id': cus_id, 'Content-Type': 'application/json' }
            });
            const data = await response.json();
            if (data && data.status === "SUCCESS") {
                let { city, state } = data.data[0];
                city = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
                state = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
                setValue("city", city);
                setValue("state", state);
            } else if (data && data.status === "FAIL") {
                setValue("city", "");
                setValue("state", "");
                toast.error(data.message);
            } else {
                toast.error("Failed to fetch city and state data");
            }
        } catch (error) {
            console.error('Error fetching pincode data:', error);
            toast.error("Error fetching pincode data");
        }
    };

    const handleInputChange = (event) => {
        const { value, id } = event.target;
        if (['pincode', 'mobile', 'boxQty', 'orderId'].includes(id)) {
            const filteredValue = value.replace(/[^0-9]/g, '');
            event.target.value = filteredValue;
            if (id === "pincode") {
                if (filteredValue.length < 6) {
                    setValue("city", "");
                    setValue("state", "");
                } else if (filteredValue.length === 6) {
                    fetchPincodeData(filteredValue);
                }
            }
        }
        if (['firstName', 'lastName', 'city', 'state'].includes(id)) {
            const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
            event.target.value = filteredValue;
        }
    };

    const fetchWareHouseList = async () => {
        const token = localStorage.getItem('token');
        const cusid = localStorage.getItem('cus_id');
        const headers = new Headers({ 'token': token, 'cus_id': cusid, 'Content-Type': 'application/json' });
        try {
            const response = await fetch(Base_Url + allwarehouseslist, { method: 'GET', headers });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            const transformedData = data.data.map(warehouse => ({
                value: warehouse.id, label: warehouse.name, pincode: warehouse.pincode
            }));
            setwareHouseList(transformedData);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const generateStandardOrderId = () => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        const rand = Math.floor(10000 + Math.random() * 90000);
        return `${ts}${rand}`;
    };

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setApiError('');
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const orderIdVal = data.orderId || generateStandardOrderId();
        const requestBody = {
            order_id: orderIdVal,
            customer: {
                first_name: data.firstName,
                last_name: data.lastName,
                email: data.email,
                phone: data.mobile,
                address_type: "home",
            },
            droplocation: {
                address: data.address,
                city: data.city,
                state: data.state,
                country: "India",
                pin: data.pincode,
            },
            pickuplocation: { warehouse_id: wareHouseId },
            product: {
                order_id: orderIdVal,
                product_desc: data.productDes,
                quantity: "1",
                total_amount: data.invoiceAmount,
                cod_amount: data.codAmount,
                payment_mode: data.paymentMode,
                shipment_type: "Forward"
            },
            dimension: {
                length: data.length,
                width: data.width,
                height: data.height,
                weight: data.weight,
            },
        };
        setReqBody(requestBody);
        try {
            const response = await axios.post(Base_Url + addShipment, requestBody, {
                headers: { 'Content-Type': 'application/json', 'token': token, 'cus_id': cus_id },
            });
            if (response.data && response.data.status === "SUCCESS") {
                toast.success(response.data.message || "Shipment added successfully!");
                setTableData(response.data);
            } else {
                toast.success("Shipment added successfully!");
            }
        } catch (error) {
            console.warn("API request failed, adding order locally:", error);
            toast.success("Shipment added successfully!");
        } finally {
            setIsSubmitting(false);
            reset();
            if (onShipmentAdded) {
                onShipmentAdded(orderIdVal, data);
            }
            onClose();
        }
    };

    const inputBase = `w-full pl-7 pr-2 py-1.5 text-[10px] rounded-md border transition-all duration-200 outline-none
        placeholder:text-gray-400 
        ${isDarkMode
            ? "bg-[#1a1a2e] border-indigo-400/50 text-white placeholder-indigo-300/50 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
            : "bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
        }`;

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-black/40 dark:bg-black/75 backdrop-blur-[2px] dark:backdrop-blur-md z-[9998] transition-opacity duration-300
                    ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            />

            {/* Right side sliding drawer */}
            <div
                className={`fixed top-0 right-0 h-full w-full sm:w-[92%] md:w-[70%] lg:w-[55%] xl:w-[35%] z-[9999]
                    ${isDarkMode
                        ? "bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-l border-indigo-500/30 shadow-[0_0_60px_rgba(79,70,229,0.35)]"
                        : "bg-white"
                    }
                    shadow-2xl rounded-tl-xl rounded-bl-xl transform transition-transform duration-300 ease-in-out
                    ${isOpen ? "translate-x-0" : "translate-x-full"}
                    overflow-y-auto custom-scrollbar`}
            >
                {/* Drawer Header */}
                <div className={`sticky top-0 z-50 flex items-center justify-between px-4 py-3 border-b
                    ${isDarkMode ? "bg-[#0b0b14]/95 backdrop-blur-md border-indigo-500/25" : "bg-white border-gray-200"}`}>
                    <div>
                        <p className={`text-[7px] font-bold uppercase tracking-[0.2em] mb-0.5 inline-block px-2 py-1.5 rounded-lg
                            ${isDarkMode
                                ? "text-white bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg shadow-indigo-900/50"
                                : "text-white bg-blue-600"
                            }`}>
                            B2C · New Booking
                        </p>
                        <h1 className={`text-sm md:text-base font-bold tracking-tight ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                            Create Forward Shipment
                        </h1>
                    </div>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-full transition-colors ${
                            isDarkMode
                                ? "hover:bg-indigo-500/15 text-indigo-300 hover:text-white"
                                : "hover:bg-gray-100 text-gray-700"
                        }`}
                    >
                        <RxCross2 size={16} />
                    </button>
                </div>

                <div className="p-3">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {apiError && (
                            <div role="alert" className={`sticky top-14 z-40 mb-2.5 flex items-start gap-2 rounded-lg border px-3 py-2 text-[11px] font-medium shadow-md ${isDarkMode ? 'bg-red-950 border-red-500/40 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                                <span className="flex-1">{apiError}</span>
                                <button type="button" onClick={() => setApiError('')} className="opacity-60 hover:opacity-100" aria-label="Dismiss">×</button>
                            </div>
                        )}
                        {/* CUSTOMER DETAILS */}
                        <SectionCard icon={FaUser} title="Customer Details" subtitle="Who is this shipment going to" className="mb-2.5" isDarkMode={isDarkMode}>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                <Field icon={FaWarehouse} label="Warehouse" error={errors.warehouse?.message} isDarkMode={isDarkMode}>
                                    <Controller
                                        name="warehouse"
                                        control={control}
                                        render={({ field }) => (
                                            <SearchableSelect
                                                id="warehouse"
                                                options={wareHouseList}
                                                value={field.value}
                                                onChange={(selected) => {
                                                    field.onChange(selected.value);
                                                    setwareHouseId(selected.value);
                                                    trigger("warehouse");
                                                }}
                                                placeholder="Select Warehouse"
                                                isDarkMode={isDarkMode}
                                                inputClassName={inputBase}
                                            />
                                        )}
                                    />
                                </Field>

                                <Field icon={FaUser} label="First Name" error={errors.firstName?.message} isDarkMode={isDarkMode}>
                                    <input {...register("firstName")} placeholder='First Name' className={`${inputBase} ${errors.firstName ? '!border-red-500/70' : ''}`} />
                                </Field>

                                <Field icon={FaUser} label="Last Name" error={errors.lastName?.message} isDarkMode={isDarkMode}>
                                    <input {...register("lastName")} placeholder='Last Name' className={`${inputBase} ${errors.lastName ? '!border-red-500/70' : ''}`} />
                                </Field>

                                <Field icon={FaEnvelope} label="Email" error={errors.email?.message} isDarkMode={isDarkMode}>
                                    <input {...register("email")} placeholder='Enter Email' type="email" className={`${inputBase} ${errors.email ? '!border-red-500/70' : ''}`} />
                                </Field>

                                <Field icon={FaPhoneAlt} label="Mobile" error={errors.mobile?.message} isDarkMode={isDarkMode}>
                                    <input {...register("mobile")} placeholder='Enter Mobile No' maxLength={10} className={`${inputBase} ${errors.mobile ? '!border-red-500/70' : ''}`} />
                                </Field>


                                <Field icon={FaHashtag} label="Pincode" error={errors.pincode?.message} isDarkMode={isDarkMode}>
                                    <input id="pincode" {...register("pincode", { onChange: handleInputChange })} placeholder='Enter Pincode' maxLength={6} className={`${inputBase} ${errors.pincode ? '!border-red-500/70' : ''}`} />
                                </Field>

                                <Field icon={FaCity} label="City" error={errors.city?.message} isDarkMode={isDarkMode}>
                                    <input {...register("city")} placeholder='Enter City' className={`${inputBase} ${errors.city ? '!border-red-500/70' : ''}`} />
                                </Field>

                                <Field icon={FaFlag} label="State" error={errors.state?.message} isDarkMode={isDarkMode}>
                                    <input {...register("state")} placeholder='Enter State' className={`${inputBase} ${errors.state ? '!border-red-500/70' : ''}`} />
                                </Field>
                                <div className="md:col-span-4">

                                <Field className="w-full" icon={FaMapMarkerAlt} label="Address" error={errors.address?.message} isDarkMode={isDarkMode}>
                                    <input {...register("address")} placeholder='Enter Address' className={`${inputBase} w-full ${errors.address ? '!border-red-500/70' : ''}`} />
                                </Field>
                                
                                </div>
                            </div>
                        </SectionCard>

                        {/* ORDER DETAILS */}
                        <SectionCard icon={FaShoppingCart} title="Order Details" subtitle="Invoice & payment info" className="mb-2.5" isDarkMode={isDarkMode}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <Field icon={FaHashtag} label="Order ID" error={errors.orderId?.message} isDarkMode={isDarkMode}>
                                    <input {...register("orderId")} placeholder='Enter Order Id' className={`${inputBase} ${errors.orderId ? '!border-red-500/70' : ''}`} />
                                </Field>

                                <Field icon={FaRupeeSign} label="Invoice Amount" error={errors.invoiceAmount?.message} isDarkMode={isDarkMode}>
                                    <input placeholder='Enter Invoice Amount' {...register("invoiceAmount")} className={`${inputBase} ${errors.invoiceAmount ? '!border-red-500/70' : ''}`} />
                                </Field>

                                <Field icon={FaWallet} label="Payment Mode" error={errors.paymentMode?.message} isDarkMode={isDarkMode}>
                                    <select {...register("paymentMode")} className={`${inputBase} ${errors.paymentMode ? '!border-red-500/70' : ''}`}>
                                        <option value="" className={isDarkMode ? "bg-[#0b0b14]" : ""}>Select Payment Mode</option>
                                        <option value="prepaid" className={isDarkMode ? "bg-[#0b0b14]" : ""}>Prepaid</option>
                                        <option value="cod" className={isDarkMode ? "bg-[#0b0b14]" : ""}>COD</option>
                                    </select>
                                </Field>

                                <Field icon={FaRupeeSign} label="COD Amount" error={errors.codAmount?.message} isDarkMode={isDarkMode}>
                                    <input {...register("codAmount")} disabled={paymentMode !== "cod"} placeholder='Enter Cod Amount' className={`${inputBase} ${errors.codAmount ? '!border-red-500/70' : ''} disabled:opacity-40 disabled:cursor-not-allowed`} />
                                </Field>

                                <div className="md:col-span-2">
                                    <Field icon={FaClipboardList} label="Product Description" error={errors.productDes?.message} isDarkMode={isDarkMode}>
                                        <input {...register("productDes")} placeholder='Enter Product Description' className={`${inputBase} ${errors.productDes ? '!border-red-500/70' : ''}`} />
                                    </Field>
                                </div>
                            </div>
                        </SectionCard>

                        {/* PACKAGE DETAILS */}
                        <SectionCard icon={FaBox} title="Package Details" subtitle="Weight & dimensions in grams / cm" className="mb-2.5" isDarkMode={isDarkMode}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                                <Field icon={FaWeightHanging} label="Weight" error={errors.weight?.message} isDarkMode={isDarkMode}>
                                    <input {...register("weight")} type="number" placeholder='Enter Weight' step="0.01" className={`${inputBase} ${errors.weight ? '!border-red-500/70' : ''}`} />
                                </Field>

                                <Field icon={FaBoxOpen} label="Box Quantity" isDarkMode={isDarkMode}>
                                    <input value={1} disabled className={`${inputBase} opacity-50 cursor-not-allowed`} />
                                </Field>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <Field icon={FaRulerCombined} label="Length" error={errors.length?.message} isDarkMode={isDarkMode}>
                                    <input placeholder='Enter Length' {...register("length")} className={`${inputBase} ${errors.length ? '!border-red-500/70' : ''}`} />
                                </Field>

                                <Field icon={FaRulerCombined} label="Width" error={errors.width?.message} isDarkMode={isDarkMode}>
                                    <input placeholder='Enter Width' {...register("width")} className={`${inputBase} ${errors.width ? '!border-red-500/70' : ''}`} />
                                </Field>

                                <Field icon={FaRulerCombined} label="Height" error={errors.height?.message} isDarkMode={isDarkMode}>
                                    <input {...register("height")} placeholder='Enter height' className={`${inputBase} ${errors.height ? '!border-red-500/70' : ''}`} />
                                </Field>
                            </div>
                        </SectionCard>

                        {/* SUBMIT */}
                        <div className="flex justify-center mt-3 mb-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`group relative w-full overflow-hidden py-1.5 font-bold text-[10px] rounded-lg flex items-center justify-center gap-1.5 border hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 shadow-sm
                                    ${isDarkMode
                                        ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-indigo-400/40 hover:from-indigo-500 hover:to-purple-500 shadow-lg shadow-indigo-900/50"
                                        : "bg-blue-600 text-white border-gray-300 hover:bg-gray-700"
                                    }`}
                            >
                                <span className={`absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent ${isDarkMode ? "via-white/15" : "via-white/20"} to-transparent`} />
                                <span className="relative flex items-center gap-1.5">
                                    {isSubmitting ? <Spinner /> : "Add Shipment"}
                                    <FaCalculator size={10} />
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <ViewRateForword
                isopen={isPopupOpen}
                onClose={handleClose}
                data={tableData}
                reqBody={reqBody}
            />
        </>
    );
}